import { hasMixin } from 'ts-mixer';
import { ApplicationError, ErrorCode } from '../error/application-error';
import { getLogger } from '../log/logger';
import { isUuidLike } from '../../domain/types';
const logger = getLogger();
class ValidationProvider {
    isValidEmail(value) {
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return regex.test(value);
    }
    validateValueNull(value, { name, nullOk } = {
        nullOk: true,
    }) {
        if (value === null) {
            if (!nullOk) {
                const message = `Value must not be null${this.getNameString(name)}`;
                logger.error(message);
                throw new ApplicationError({
                    code: ErrorCode.ERR_VALIDATION,
                    messageExternal: message,
                });
            }
            else {
                return true;
            }
        }
        return false;
    }
    validateValueUndefined(value, { name, undefinedOk } = {
        undefinedOk: true,
    }) {
        if (value === undefined) {
            if (!undefinedOk) {
                const message = `Value must not be undefined${this.getNameString(name)}`;
                logger.error(message);
                throw new ApplicationError({
                    code: ErrorCode.ERR_VALIDATION,
                    messageExternal: message,
                });
            }
            else {
                return true;
            }
        }
        return false;
    }
    validateAndTransformBoolean(value, options = {}) {
        const { name, nullOk, undefinedOk, required, transformNullToUndefined } = {
            ...this.getValidationOptionsDefaults(),
            ...options,
        };
        if (options.transformStringToBoolean && typeof value === 'string') {
            const lowerCaseVal = value.toLowerCase();
            if (['true', 't', 'yes', 'y', '1'].includes(lowerCaseVal)) {
                return true;
            }
            if (['false', 'f', 'no', 'n', '0'].includes(lowerCaseVal)) {
                return false;
            }
        }
        if (options.transformNullishToFalse && value == null) {
            return false;
        }
        if (options.transformTruthyAndFalsyToBoolean) {
            return !!value;
        }
        const isNullOrUndefined = this.validateValueNull(value, { name, nullOk: required ? false : nullOk }) ||
            this.validateValueUndefined(value, { name, undefinedOk: required ? false : undefinedOk });
        if (isNullOrUndefined) {
            return transformNullToUndefined ? undefined : value;
        }
        if (typeof value !== 'boolean') {
            const message = `Value must be a boolean${this.getNameString(name)}`;
            logger.error(message);
            throw new ApplicationError({
                code: ErrorCode.ERR_VALIDATION,
                messageExternal: message,
            });
        }
        return value;
    }
    validateAndTransformNumber(value, options = {}) {
        const { name, nullOk, undefinedOk, required, transformNullToUndefined } = {
            ...this.getValidationOptionsDefaults(),
            ...options,
        };
        const isNullOrUndefined = this.validateValueNull(value, { name, nullOk: required ? false : nullOk }) ||
            this.validateValueUndefined(value, { name, undefinedOk: required ? false : undefinedOk });
        if (isNullOrUndefined) {
            return transformNullToUndefined ? undefined : value;
        }
        const mustBeInteger = options.mustBeInteger ?? false;
        if (typeof value === 'number') {
            return mustBeInteger ? Math.floor(value) : value;
        }
        else if (typeof value === 'string') {
            if (!value) {
                const message = `Value must be a number${this.getNameString(name)}`;
                logger.error(message);
                throw new ApplicationError({
                    code: ErrorCode.ERR_VALIDATION,
                    messageExternal: message,
                });
            }
            const numberValue = Number(value);
            if (isNaN(numberValue)) {
                const message = `Value must be a number${this.getNameString(name)}`;
                logger.error(message);
                throw new ApplicationError({
                    code: ErrorCode.ERR_VALIDATION,
                    messageExternal: message,
                });
            }
            return mustBeInteger ? Math.floor(numberValue) : numberValue;
        }
        else {
            const message = `Value must be a number${this.getNameString(name)}`;
            logger.error(message);
            throw new ApplicationError({
                code: ErrorCode.ERR_VALIDATION,
                messageExternal: message,
            });
        }
    }
    validateAndTransformString(value, options = {}) {
        const { name, nullOk, undefinedOk, required, transformNullToUndefined } = {
            ...this.getValidationOptionsDefaults(),
            ...options,
        };
        const isNullOrUndefined = this.validateValueNull(value, { name, nullOk: required ? false : nullOk }) ||
            this.validateValueUndefined(value, { name, undefinedOk: required ? false : undefinedOk });
        if (isNullOrUndefined) {
            return transformNullToUndefined ? undefined : value;
        }
        return `${value}`;
    }
    validateAndTransformDateString(value, options = {}) {
        const { name, nullOk, undefinedOk, required, transformNullToUndefined } = {
            ...this.getValidationOptionsDefaults(),
            ...options,
        };
        const isNullOrUndefined = this.validateValueNull(value, { name, nullOk: required ? false : nullOk }) ||
            this.validateValueUndefined(value, { name, undefinedOk: required ? false : undefinedOk });
        if (isNullOrUndefined) {
            return transformNullToUndefined ? undefined : value;
        }
        if (typeof value !== 'string') {
            const message = `Value must be a string${this.getNameString(name)}`;
            logger.error(message);
            throw new ApplicationError({
                code: ErrorCode.ERR_VALIDATION,
                messageExternal: message,
            });
        }
        if (!this.isDateISOString(value)) {
            const message = `Value must be a valid date string in format 'YYYY-MM-DDTHH:mm:ss.sssZ'${this.getNameString(name)}`;
            logger.error(message);
            throw new ApplicationError({
                code: ErrorCode.ERR_VALIDATION,
                messageExternal: message,
            });
        }
        return value;
    }
    isDateISOString(input) {
        const utcFormatRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;
        if (!utcFormatRegex.test(input))
            return false;
        const date = new Date(input);
        if (isNaN(date.getTime()))
            return false;
        const year = date.getUTCFullYear();
        const month = String(date.getUTCMonth() + 1).padStart(2, '0');
        const day = String(date.getUTCDate()).padStart(2, '0');
        const hour = String(date.getUTCHours()).padStart(2, '0');
        const minute = String(date.getUTCMinutes()).padStart(2, '0');
        const second = String(date.getUTCSeconds()).padStart(2, '0');
        const millisecond = String(date.getUTCMilliseconds()).padStart(3, '0');
        const expectedFormat = `${year}-${month}-${day}T${hour}:${minute}:${second}.${millisecond}Z`;
        return input === expectedFormat;
    }
    validateAndTransformId(value, options = {}) {
        const { name, nullOk, undefinedOk, required, transformNullToUndefined } = {
            ...this.getValidationOptionsDefaults(),
            ...options,
        };
        const isNullOrUndefined = this.validateValueNull(value, { name, nullOk: required ? false : nullOk }) ||
            this.validateValueUndefined(value, { name, undefinedOk: required ? false : undefinedOk });
        if (isNullOrUndefined) {
            return transformNullToUndefined ? undefined : value;
        }
        if (typeof value !== 'string') {
            const message = `Value must be a Tp3Id${this.getNameString(name)}`;
            logger.error(message);
            throw new ApplicationError({
                code: ErrorCode.ERR_VALIDATION,
                messageExternal: message,
            });
        }
        if (!isUuidLike(value)) {
            const message = `Value is not a valid uuid${this.getNameString(name)}`;
            logger.error(message);
            throw new ApplicationError({
                code: ErrorCode.ERR_VALIDATION,
                messageExternal: message,
            });
        }
        return value;
    }
    validateAndTransformEnumValue(value, enumValues, options = {}) {
        const { name, nullOk, undefinedOk, required, transformNullToUndefined } = {
            ...this.getValidationOptionsDefaults(),
            ...options,
        };
        const isNullOrUndefined = this.validateValueNull(value, { name, nullOk: required ? false : nullOk }) ||
            this.validateValueUndefined(value, { name, undefinedOk: required ? false : undefinedOk });
        if (isNullOrUndefined) {
            return transformNullToUndefined ? undefined : value;
        }
        if (!enumValues.includes(value)) {
            const enumValuesString = enumValues.join(', ');
            const message = `Value is not valid: ${value}. ${this.getNameString(name)} must be one of ${enumValuesString}`;
            logger.error(message);
            throw new ApplicationError({
                code: ErrorCode.ERR_VALIDATION,
                messageExternal: message,
            });
        }
        return value;
    }
    validateAndTransformInstanceOfClass(value, classType, options = {}) {
        const { name, nullOk, undefinedOk, required, transformNullToUndefined } = {
            ...this.getValidationOptionsDefaults(),
            ...options,
        };
        const isNullOrUndefined = this.validateValueNull(value, { name, nullOk: required ? false : nullOk }) ||
            this.validateValueUndefined(value, { name, undefinedOk: required ? false : undefinedOk });
        if (isNullOrUndefined) {
            return transformNullToUndefined ? undefined : value;
        }
        if (!(value instanceof classType) && !hasMixin(value, classType)) {
            const message = `Value must be an instance of ${classType.name}${this.getNameString(name)}`;
            logger.error(message);
            throw new ApplicationError({
                code: ErrorCode.ERR_VALIDATION,
                messageExternal: message,
            });
        }
        return value;
    }
    validateAndTransformObject(value, options = {}) {
        const { name, nullOk, undefinedOk, required, transformNullToUndefined } = {
            ...this.getValidationOptionsDefaults(),
            ...options,
        };
        const transformNullishToEmptyObject = options.transformNullishToEmptyObject ?? false;
        const isNullOrUndefined = this.validateValueNull(value, { name, nullOk: required ? false : nullOk }) ||
            this.validateValueUndefined(value, { name, undefinedOk: required ? false : undefinedOk });
        if (isNullOrUndefined) {
            if (transformNullishToEmptyObject) {
                return {};
            }
            return transformNullToUndefined
                ? undefined
                : value;
        }
        if (typeof value !== 'object' ||
            value === null ||
            Array.isArray(value) ||
            value.constructor !== Object) {
            const message = `Value must be an object${this.getNameString(name)}`;
            logger.error(message);
            throw new ApplicationError({
                code: ErrorCode.ERR_VALIDATION,
                messageExternal: message,
            });
        }
        if (options.transformPropertiesEmptyStringToNull) {
            const objValue = value;
            for (const key in objValue) {
                if (objValue[key] === '') {
                    objValue[key] = null;
                }
            }
        }
        return value;
    }
    validateAndTransformEmail(value, options = {}) {
        const { name, nullOk, undefinedOk, required, transformNullToUndefined } = {
            ...this.getValidationOptionsDefaults(),
            ...options,
        };
        const isNullOrUndefined = this.validateValueNull(value, { name, nullOk: required ? false : nullOk }) ||
            this.validateValueUndefined(value, { name, undefinedOk: required ? false : undefinedOk });
        if (isNullOrUndefined) {
            return transformNullToUndefined ? undefined : value;
        }
        if (typeof value !== 'string') {
            const message = `Value must be a string${this.getNameString(name)}`;
            logger.error(message);
            throw new ApplicationError({
                code: ErrorCode.ERR_VALIDATION,
                messageExternal: message,
            });
        }
        if (!this.isValidEmail(value)) {
            const message = `Value is not a valid email${this.getNameString(name)}`;
            logger.error(message);
            throw new ApplicationError({
                code: ErrorCode.ERR_VALIDATION,
                messageExternal: message,
            });
        }
        return value;
    }
    getValidationOptionsDefaults() {
        return {
            nullOk: true,
            undefinedOk: true,
            required: false,
            transformNullToUndefined: true,
        };
    }
    getNameString(name) {
        return name ? `: ${name}` : '.';
    }
}
const validationProvider = new ValidationProvider();
export { validationProvider };
//# sourceMappingURL=validation.provider.js.map